/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from 'react';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  ClassicEditor,
  Emoji,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Indent,
  Italic,
  Link,
  List,
  ListProperties,
  Mention,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  Strikethrough,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline
} from "ckeditor5";
import axios from 'axios';
import axiosInstance from '../../../axiosConfig';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import "ckeditor5/ckeditor5.css";
import "../css/destination.css";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

class LocationEditor extends ClassicEditor {}

LocationEditor.builtinPlugins = [
  Essentials,
  Paragraph,
  Heading,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  List,
  ListProperties,
  BlockQuote,
  Alignment,
  Indent,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  PasteFromOffice,
  Mention,
  Emoji,
  RemoveFormat
];

LocationEditor.defaultConfig = {
  licenseKey: "GPL",
  toolbar: {
    items: [
      "heading",
      "|",
      "fontFamily",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "removeFormat",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "outdent",
      "indent",
      "|",
      "link",
      "emoji",
      "blockQuote",
      "insertTable",
      "|",
      "undo",
      "redo"
    ],
    shouldNotGroupWhenFull: true
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" }
    ]
  },
  fontFamily: {
    supportAllValues: true
  },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 22, 24, 28, 32],
    supportAllValues: true
  },
  fontColor: {
    columns: 8,
    documentColors: 10
  },
  fontBackgroundColor: {
    columns: 8,
    documentColors: 10
  },
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://"
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties"
    ]
  }
};

const LocationForm = ({ destinationUuid, setActiveTab, isViewMode }) => {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedAttractions, setSelectedAttractions] = useState([]);
  const [attractionsDropdownOpen, setAttractionsDropdownOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [cityData, setCityData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapPosition, setMapPosition] = useState([6.9271, 79.8612]); // Default to Sri Lanka
  const mapRef = useRef(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [metaData, setMetaData] = useState({
    cities: [] // Initialize with an empty categories array
  });
  const [formdata, setFormData] = useState({
    cityId: "1",
    fullAddress: "",
    locationInfo: "", // Added missing field
    locationAccessGuide: "", // Added missing field
    geotag: {
      latitude: "",
      longitude: "",
      altitude: "4",
      name: ""
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'latitude' || name === 'longitude' || name === 'altitude' || name === 'name') {
      setFormData((prevData) => ({
        ...prevData,
        geotag: {
          ...prevData.geotag,
          [name]: value,
        },
      }));
      
      // Update map position when coordinates are manually entered
      if (name === 'latitude' || name === 'longitude') {
        const lat = name === 'latitude' ? parseFloat(value) : parseFloat(formdata.geotag.latitude);
        const lng = name === 'longitude' ? parseFloat(value) : parseFloat(formdata.geotag.longitude);
        
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          setMapPosition([lat, lng]);
          // Pan map to new position
          if (mapRef.current) {
            mapRef.current.flyTo([lat, lng], 13);
          }
        }
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleRichTextChange = (name) => (_, editor) => {
    const value = editor.getData();
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle map click to update coordinates
  const handleMapClick = (lat, lng) => {
    setMapPosition([lat, lng]);
    setFormData((prevData) => ({
      ...prevData,
      geotag: {
        ...prevData.geotag,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      const dataToSend = {
        ...formdata,
        cityId: parseInt(formdata.cityId),
        destinationUuid: destinationUuid
      };

      console.log("Sending data:", dataToSend); // Debug log

      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/destination-panel/registration/destination-location',
        dataToSend
      );
      console.log("Response:", response.data);
      setActiveTab("images");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // Fetch cities and attractions data from API
  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        console.log("Fetching metadata...");
        const token = localStorage.getItem('token');
        console.log(token);

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/destination-panel/registration/meta',
        );

        if (response.data && response.data.result) {
          setMetaData(response.data.result);
          console.log("Cities:", response.data.result);
        }

        console.log("Response:", response.data);
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setError(error.message || "Failed to fetch data");
      } finally {
        setIsLoading(false); // Set loading to false when fetch completes
      }
    };

    const fetchDestinationLocation = async () => {
      if (destinationUuid) {
        try {
          const response = await axiosInstance.get(
            `/internal-api/v1/client-web/destination-panel/config/${destinationUuid}/view`
          );
          if (response.data && response.data.result) {
            const data = response.data.result;
            setFormData({
              cityId: data.destination?.city?.id?.toString() || "1",
              fullAddress: data.detail?.geoLocation?.address || "",
              locationInfo: data.detail?.locationInfo || "",
              locationAccessGuide: data.detail?.locationAccessGuide || "",
              geotag: {
                latitude: data.detail?.geoLocation?.latitude?.toString() || "",
                longitude: data.detail?.geoLocation?.longitude?.toString() || "",
                altitude: data.detail?.geoLocation?.altitude?.toString() || "4",
                name: data.detail?.geoLocation?.name || ""
              }
            });
            if (data.detail?.geoLocation?.latitude && data.detail?.geoLocation?.longitude) {
              setMapPosition([data.detail.geoLocation.latitude, data.detail.geoLocation.longitude]);
            }
            setIsReadOnly(isViewMode);
            setSelectedCity(data.destination?.city?.id?.toString() || '');
          }
        } catch (error) {
          console.error("Error fetching destination location:", error);
        }
      }
    };

    fetchMetaData();
    fetchDestinationLocation();
  }, [destinationUuid, isViewMode]);

  useEffect(() => {
    if (isViewMode) {
      setIsReadOnly(true);
    }
  }, [isViewMode]);

  const handleCityChange = (e) => {
    handleChange(e);
    const city = e.target.value;
    setSelectedCity(city);
    setSelectedAttractions([]);
  };

  const handleAttractionChange = (attraction) => {
    if (selectedAttractions.includes(attraction)) {
      setSelectedAttractions(selectedAttractions.filter(item => item !== attraction));
    } else {
      setSelectedAttractions([...selectedAttractions, attraction]);
    }
  };

  const removeAttraction = (attraction) => {
    setSelectedAttractions(selectedAttractions.filter(item => item !== attraction));
  };

  // Toggle the dropdown
  const toggleAttractionsDropdown = () => {
    setAttractionsDropdownOpen(!attractionsDropdownOpen);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownContainer = document.getElementById('attractions-dropdown-container');
      if (dropdownContainer && !dropdownContainer.contains(event.target)) {
        setAttractionsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll to top when component loads
  useEffect(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="location-form">
      {!destinationUuid ? (
        <div className="p-6">
          <div className="p-6 text-center rounded bg-blue-50">
            <h3 className="mb-3 text-base font-medium text-gray-700">Location Details</h3>
            <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable location management.</p>
            <p className="mt-3 text-xs text-gray-600">After saving the destination, you will be able to add location information with:</p>
            <p className="mt-2 text-xs text-gray-600">
              • City and address details • Geographic coordinates • Interactive map location • Access guide
            </p>
          </div>
        </div>
      ) : (
      <div className="location-form-container">
        <h2 className="location-form-title">Location Details</h2>

        {/* Loading indicator */}
        {isLoading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <span className="loading-text">Loading cities...</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="error-container">
            <p className="error-text">Error loading cities: {error}</p>
            <p className="error-text">Please try again later or contact support.</p>
          </div>
        )}

        {/* Form Content */}
        <div className="form-content">
          <div className="form-field">
            <label className="form-label">
              City <span className="required-asterisk">*</span>
            </label>
            <div className="select-container" style={{ position: 'relative', display: 'inline-block', width: '48%' }}>
              <select
                className="form-select"
                value={selectedCity}
                name='cityId'
                onChange={(e) => {
                  e.target.value = parseInt(e.target.value);
                  handleCityChange(e);
                }}
                disabled={isLoading || isReadOnly}
                style={{ 
                  appearance: 'none', 
                  WebkitAppearance: 'none', 
                  MozAppearance: 'none',
                  width: '100%',
                  paddingRight: '2.5rem',
                  background: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  height: '2.5rem'
                }}
              >
                <option value="" disabled>Select a city</option>
                {metaData && metaData.cities && metaData.cities.length > 0 ? (
                  metaData.cities.map((city) => (
                    <option key={city.id} value={parseInt(city.id)}>{city.name}</option>
                  ))
                ) : (
                  <option value="" disabled>No cities available</option>
                )}
              </select>
              {/* Single dropdown arrow */}
              <div 
                className="select-arrow"
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '0.75rem',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                <svg 
                  className="select-arrow-icon" 
                  viewBox="0 0 20 20"
                  style={{
                    width: '1rem',
                    height: '1rem',
                    fill: '#6b7280'
                  }}
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="form-field">
            <label className="form-label">
              Full Address <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              name="fullAddress"
              value={formdata.fullAddress}
              onChange={handleChange}
              placeholder="Enter complete address"
              className="form-input"
              disabled={isReadOnly}
            />
          </div>

          {/* Added Location Access Guide field */}
          <div className="form-field">
            <label className="form-label">
              Location Access Guide <span className="required-asterisk">*</span>
            </label>
            {isReadOnly ? (
              <div
                className="rich-text-preview"
                dangerouslySetInnerHTML={{
                  __html: formdata.locationAccessGuide || "<p></p>"
                }}
              />
            ) : (
              <div className="rich-text-editor">
                <CKEditor
                  editor={LocationEditor}
                  data={formdata.locationAccessGuide || ""}
                  config={{
                    placeholder: "Provide instructions on how to reach this destination"
                  }}
                  onChange={handleRichTextChange("locationAccessGuide")}
                />
              </div>
            )}
          </div>

          {/* Added Location Info field */}
          <div className="form-field">
            <label className="form-label">
              Travel Tips and Information <span className="required-asterisk">*</span>
            </label>
            {isReadOnly ? (
              <div
                className="rich-text-preview"
                dangerouslySetInnerHTML={{
                  __html: formdata.locationInfo || "<p></p>"
                }}
              />
            ) : (
              <div className="rich-text-editor">
                <CKEditor
                  editor={LocationEditor}
                  data={formdata.locationInfo || ""}
                  config={{
                    placeholder: "Describe Travel Tips, Needs and other relevant information for travelers visiting this destination"
                  }}
                  onChange={handleRichTextChange("locationInfo")}
                />
              </div>
            )}
          </div>

          

          <div className="form-field">
            <label className="map-label">
              Map Location
            </label>
            <p className="map-description">Click on the map to set the exact location</p>
            <MapComponent 
              position={mapPosition} 
              onMapClick={isReadOnly ? () => {} : handleMapClick}
              mapRef={mapRef}
            />
          </div>

          <div className="coordinates-container">
            <div className="coordinate-field">
              <label className="form-label">
                Latitude <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                name='latitude'
                value={formdata.geotag.latitude}
                onChange={handleChange}
                placeholder="e.g., 12.971598"
                className="form-input"
                disabled={isReadOnly}
              />
            </div>
            <div className="coordinate-field">
              <label className="form-label">
                Longitude <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                name='longitude'
                value={formdata.geotag.longitude}
                onChange={handleChange}
                placeholder="e.g., 77.594566"
                className="form-input"
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Second row with Location Name and Nearby Attractions */}
          <div className="coordinates-container">
            <div className="coordinate-field">
              <label className="form-label">
                Location Name
              </label>
              <input
                type="text"
                name='name'
                value={formdata.geotag.name}
                onChange={handleChange}
                placeholder="e.g., Beach Park"
                className="form-input"
                disabled={isReadOnly}
              />
            </div>
            
            {/* <div className="coordinate-field" style={{ position: 'relative' }}>
              <label className="form-label">
                Nearby Attractions
              </label>
              <div id="attractions-dropdown-container" style={{ position: 'relative', width: '100%' }}>
                <div
                  className={`dropdown-button ${attractionsDropdownOpen ? 'active' : ''} ${!selectedCity || isLoading ? 'disabled' : ''}`}
                  onClick={(selectedCity && !isLoading) ? toggleAttractionsDropdown : undefined}
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    backgroundColor: 'white',
                    cursor: (!selectedCity || isLoading) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span className={`dropdown-text ${!selectedCity || isLoading ? 'disabled' : ''}`}>
                    {isLoading ? "Loading..." :
                      selectedCity
                        ? selectedAttractions.length > 0
                          ? `${selectedAttractions.length} attraction${selectedAttractions.length > 1 ? 's' : ''} selected`
                          : "Select attractions"
                        : "Please select a city first"
                    }
                  </span>
                  <svg
                    className={`dropdown-arrow ${attractionsDropdownOpen ? 'rotated' : ''}`}
                    viewBox="0 0 20 20"
                    style={{ width: '1.25rem', height: '1.25rem', fill: 'currentColor' }}
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div> */}

                {/* Dropdown menu */}
                {/* {attractionsDropdownOpen && selectedCity && !isLoading && (
                  <div className="dropdown-menu" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {cityData[selectedCity]?.attractions?.length > 0 ? (
                      cityData[selectedCity].attractions.map((attraction) => (
                        <div
                          key={attraction}
                          className="dropdown-item"
                          onClick={() => handleAttractionChange(attraction)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAttractions.includes(attraction)}
                            onChange={() => { }}
                            className="dropdown-checkbox"
                            style={{ marginRight: '0.5rem' }}
                          />
                          <span className="dropdown-item-text">{attraction}</span>
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-empty" style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        color: '#6b7280'
                      }}>
                        No attractions found for this city
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div> */}
          </div>

          {/* Display selected attractions as tags */}
          {selectedAttractions.length > 0 && (
            <div className="selected-tags">
              {selectedAttractions.map(attraction => (
                <div key={attraction} className="tag">
                  <span>{attraction}</span>
                  <button
                    type="button"
                    onClick={() => removeAttraction(attraction)}
                    className="tag-remove-btn"
                  >
                    <svg className="tag-remove-icon" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-footer">
          <div className="required-note">* Required fields</div>
          <div className="button-group">
            <button className="btn-secondary" disabled={isReadOnly}>
              Previous
            </button>
            {!isReadOnly && (
              <button
                onClick={() => handleSubmit()}
                className="btn-primary"
                disabled={isLoading}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// MapComponent to handle map interactions
const MapComponent = ({ position, onMapClick, mapRef }) => {
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        onMapClick(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  };

  return (
    <div style={{ height: '400px', width: '100%', borderRadius: '0.375rem', overflow: 'hidden', border: '1px solid #d1d5db' }}>
      <MapContainer
        center={position}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={position} />
        <MapClickHandler />
      </MapContainer>
    </div>
  );
};

export default LocationForm;
